import { FormControlLabel, Grid, Switch, Tab, Tabs } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { Checkbox } from 'src/components/shared/Checkbox/Checkbox';
import { Divider } from 'src/components/shared/Divider/Divider';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { TabPanel } from 'src/components/shared/Tabs/TabPanel';
import {
  GradeCorrectionDto,
  GradeDetailDto,
  GradeParameterDto
} from 'src/store/grades/grade';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';
import { GradeCorrections } from './GradeCorrections';
import { GradeParameters } from './GradeParameters';

type Props = {
  grade: Nullable<GradeDetailDto>;
  onCancel: () => void;
  onSubmit: (instrument: GradeDetailDto) => void;
};

export const GradeForm: React.FC<Props> = props => {
  const { onSubmit, onCancel } = props;
  const { t } = useTranslation();

  const [tab, setTab] = useState<number>(0);

  const [grade, setGrade] = useState<GradeDetailDto>(
    props.grade ||
      ({
        active: true,
        corrected: false,
        internal: false
      } as GradeDetailDto)
  );

  const formikRef = useRef<FormikProps<GradeDetailDto>>(null);

  useEffect(() => {
    if (props.grade) setGrade(props.grade);
  }, [props.grade]);

  const validate = (grade: GradeDetailDto) => {
    const validationRunners = [
      validationRunner('no', t('props.number'), required),
      validationRunner('name', t('props.name'), required, maxLength(50)),
      validationRunner('description', t('props.description'), maxLength(200))
    ];
    return runValidations(grade, validationRunners);
  };

  const onGradeParametersChanged = (parameters: GradeParameterDto[]) => {
    formikRef?.current?.setFieldValue('gradeParameters', parameters);
  };

  const onGradeCorrectionsChanged = (corrections: GradeCorrectionDto[]) => {
    formikRef?.current?.setFieldValue('gradeCorrections', corrections);
  };

  const submit = () => {
    formikRef?.current?.submitForm();
  };

  return (
    <Fragment>
      <Formik
        initialValues={grade}
        innerRef={formikRef}
        validate={validate}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          submitCount
        }) => (
          <Form onSubmit={handleSubmit}>
            <Grid container>
              <Grid item xs={3}>
                <InputField
                  name="no"
                  label={t('props.number')}
                  value={values.no}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.no}
                  displayError={touched.no || submitCount > 0}
                />
              </Grid>
              <Grid item xs={9}>
                <InputField
                  name="name"
                  label={t('props.name')}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.name}
                  displayError={touched.name || submitCount > 0}
                />
              </Grid>
            </Grid>
            <InputField
              name="description"
              label={t('props.description')}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.description}
              displayError={touched.description || submitCount > 0}
            />
            <div className="mb-24">
              <FormControlLabel
                control={
                  <Switch
                    checked={values.active}
                    onChange={handleChange}
                    name="active"
                  />
                }
                label={t('props.active')}
              />
              <Checkbox
                label={t('grade.corrected')}
                checked={values.corrected}
                name="corrected"
                onChange={handleChange}
              />
              <Checkbox
                label={t('props.internal')}
                checked={values.internal}
                name="internal"
                onChange={handleChange}
              />
            </div>

            <Divider />
          </Form>
        )}
      </Formik>

      <Tabs
        value={tab}
        onChange={(_, tab) => setTab(tab)}
        aria-label="Product tabs"
      >
        <Tab label={t('parameter.plural')} />
        <Tab label={t('grade.corrections')} />
      </Tabs>

      <TabPanel index={0} value={tab}>
        <GradeParameters
          grade={grade}
          onParametersChanged={parameters => {
            onGradeParametersChanged(parameters);
          }}
        />
      </TabPanel>
      <TabPanel index={1} value={tab}>
        <GradeCorrections
          grade={grade}
          onCorrectionsChanged={corrections => {
            onGradeCorrectionsChanged(corrections);
          }}
        />
      </TabPanel>

      <ModalFooter>
        <TertiaryButton
          onClick={onCancel}
          label={t('cancel')}
          className="mr-32"
        />
        <PrimaryButton onClick={submit} label={t('save.verb')} />
      </ModalFooter>
    </Fragment>
  );
};
